import React from 'react';
import { Move, Lock, CalendarX2, CalendarOff, X, ShoppingBag, Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Modalità operative dell'agenda (una sola attiva alla volta)
export type AgendaMode = 'none' | 'move-pick' | 'move-target' | 'block' | 'cancelled';

interface CalendarModeToolbarProps {
    mode: AgendaMode;
    view: 'day' | 'week';
    /** Etichetta per il banner di spostamento in corso: "Nome cliente (HH:mm)" */
    movingLabel?: string | null;
    onStartMove: () => void;
    onToggleBlock: () => void;
    onToggleCancelled: () => void;
    /** Apre la modale di chiusura giornata (non è una modalità agenda) */
    onOpenCloseDay: () => void;
    onExit: () => void;
    /** Vendita al banco segnata al volo, senza uscire dall'agenda */
    onQuickSale?: () => void;
    /** Manda un messaggio a chi ha prenotato nel giorno visualizzato (solo titolare) */
    onNotifyClients?: () => void;
}

/**
 * Toolbar delle modalità agenda (stile Clip Workspace): Sposta, Blocca slot,
 * Cancellati, Chiudi giornata. Sotto, il banner della modalità attiva con "Esci".
 */
const CalendarModeToolbar: React.FC<CalendarModeToolbarProps> = ({
    mode, view, movingLabel, onStartMove, onToggleBlock, onToggleCancelled, onOpenCloseDay, onExit, onQuickSale,
    onNotifyClients,
}) => {
    const { t } = useTranslation();

    const isMove = mode === 'move-pick' || mode === 'move-target';

    const btnBase = 'flex items-center gap-1.5 px-2.5 py-1 md:px-3 md:py-1.5 rounded-lg text-xs font-bold transition-colors whitespace-nowrap';
    const btnOff = 'text-gray-600 hover:bg-gray-50 hover:text-gray-900';

    const banners: Record<string, { cls: string; title: string; text: string } | undefined> = {
        'move-pick': {
            cls: 'bg-clip-50 border-clip-200 text-clip-700',
            title: t('calendar.toolbar.move', 'Sposta'),
            text: t('calendar.toolbar.move_pick_hint', "Tocca l'appuntamento che vuoi spostare."),
        },
        'move-target': {
            cls: 'bg-clip-50 border-clip-200 text-clip-700',
            title: t('calendar.toolbar.move_target_title', 'Spostamento in corso'),
            text: movingLabel
                ? t('calendar.toolbar.move_target_hint_named', 'Tocca uno slot libero (verde) dove spostare {{label}}.', { label: movingLabel })
                : t('calendar.toolbar.move_target_hint', 'Tocca uno slot libero (verde) di destinazione.'),
        },
        block: {
            cls: 'bg-gray-50 border-gray-200 text-gray-700',
            title: t('calendar.toolbar.block', 'Blocca slot'),
            text: t('calendar.block_mode_hint', 'Tocca uno slot bianco per bloccarlo, o uno grigio per renderlo disponibile.'),
        },
        cancelled: {
            cls: 'bg-red-50 border-red-200 text-red-700',
            title: t('calendar.toolbar.cancelled', 'Cancellati'),
            text: t('calendar.toolbar.cancelled_hint', 'Agenda in sola lettura: negli slot vedi chi è stato cancellato e quando.'),
        },
    };
    const banner = banners[mode];

    return (
        <div className="border-b border-gray-100">
            {/* Niente scroll orizzontale: i bottoni vanno a capo (max due righe su mobile) */}
            <div className="flex items-center flex-wrap gap-x-1 gap-y-0.5 px-2 py-1 md:px-4 md:py-1.5">
                <button
                    type="button"
                    onClick={isMove ? onExit : onStartMove}
                    className={`${btnBase} ${isMove ? 'bg-clip-500 text-white shadow-sm' : btnOff}`}
                >
                    <Move size={14} /> {t('calendar.toolbar.move', 'Sposta')}
                </button>
                <button
                    type="button"
                    onClick={onToggleBlock}
                    className={`${btnBase} ${mode === 'block' ? 'bg-gray-900 text-white shadow-sm' : btnOff}`}
                >
                    <Lock size={14} /> {t('calendar.toolbar.block', 'Blocca slot')}
                </button>
                <button
                    type="button"
                    onClick={onToggleCancelled}
                    className={`${btnBase} ${mode === 'cancelled' ? 'bg-red-500 text-white shadow-sm' : btnOff}`}
                >
                    <CalendarX2 size={14} /> {t('calendar.toolbar.cancelled', 'Cancellati')}
                </button>
                {view === 'day' && (
                    <button
                        type="button"
                        onClick={onOpenCloseDay}
                        className={`${btnBase} ${btnOff}`}
                    >
                        <CalendarOff size={14} /> {t('calendar.toolbar.editday', 'Chiudi giornata')}
                    </button>
                )}

                {/* Scorciatoia: apre l'invio notifiche già filtrato sul giorno visualizzato */}
                {onNotifyClients && (
                    <button
                        type="button"
                        onClick={onNotifyClients}
                        className={`${btnBase} ${btnOff}`}
                    >
                        <Bell size={14} /> {t('calendar.toolbar.notify_clients', 'Avvisa i clienti')}
                    </button>
                )}

                {onQuickSale && (
                    <button
                        type="button"
                        onClick={onQuickSale}
                        className={`${btnBase} ml-auto flex-shrink-0 bg-gray-900 text-white hover:bg-black shadow-sm`}
                    >
                        <ShoppingBag size={14} /> {t('calendar.toolbar.quick_sale', 'Segna vendita')}
                    </button>
                )}
            </div>

            {banner && (
                <div className={`flex items-center justify-between gap-3 px-3 py-1.5 md:px-4 border-t text-xs font-medium ${banner.cls}`}>
                    <span className="min-w-0 truncate">
                        <span className="font-bold">{banner.title}.</span> {banner.text}
                    </span>
                    <button
                        type="button"
                        onClick={onExit}
                        className="flex items-center gap-1 flex-shrink-0 px-2.5 py-1 rounded-lg bg-white/80 border border-current/20 font-bold hover:bg-white transition-colors"
                    >
                        <X size={12} /> {t('calendar.toolbar.exit', 'Esci')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default CalendarModeToolbar;
